import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { API, useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { Plus, Edit, Trash2, X, Save, FileText, Lock, Unlock, Upload, Loader2, Image } from 'lucide-react';

export default function ManageArticles() {
  const { token } = useAuth();
  const { currentColors } = useTheme();
  const [articles, setArticles] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const imageInputRef = useRef(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    author: '',
    category: '',
    image_url: '',
    is_premium: false
  });

  useEffect(() => {
    loadArticles();
  }, []);

  const loadArticles = async () => {
    try {
      const response = await axios.get(`${API}/articles`);
      setArticles(response.data);
    } catch (error) {
      console.error('Failed to load articles:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${API}/articles/${editingId}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API}/articles`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      resetForm();
      loadArticles();
    } catch (error) {
      console.error('Failed to save article:', error);
      alert('Failed to save article');
    }
  };

  const handleEdit = (article) => {
    setFormData({
      title: article.title,
      content: article.content,
      author: article.author,
      category: article.category,
      image_url: article.image_url || '',
      is_premium: article.is_premium || false
    });
    setEditingId(article.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this article?')) return;
    try {
      await axios.delete(`${API}/articles/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadArticles();
    } catch (error) {
      console.error('Failed to delete article:', error);
    }
  };

  const resetForm = () => {
    setFormData({ title: '', content: '', author: '', category: '', image_url: '', is_premium: false });
    setEditingId(null);
    setShowForm(false);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadingImage(true);
    const uploadData = new FormData();
    uploadData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/upload/image`, uploadData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setFormData(prev => ({ ...prev, image_url: response.data.url }));
    } catch (error) {
      alert(error.response?.data?.detail || 'Image upload failed');
    } finally {
      setUploadingImage(false);
      e.target.value = '';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <FileText className="w-6 h-6" style={{ color: currentColors.primary }} />
          <h2 className="text-2xl font-cinzel" style={{ color: currentColors.primary }}>
            Manage Articles
          </h2>
        </div>
        <button onClick={() => setShowForm(!showForm)} className="cosmic-btn flex items-center gap-2">
          {showForm ? <X className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
          <span>{showForm ? 'Cancel' : 'Add New'}</span>
        </button>
      </div>

      {showForm && (
        <div className="glass-card p-6">
          <h3 className="text-xl font-cinzel mb-4" style={{ color: currentColors.text }}>
            {editingId ? 'Edit Article' : 'Add New Article'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>Title *</label>
                <input type="text" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} className="cosmic-input w-full" required />
              </div>
              <div>
                <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>Author *</label>
                <input type="text" value={formData.author} onChange={(e) => setFormData({ ...formData, author: e.target.value })} className="cosmic-input w-full" required />
              </div>
            </div>
            <div>
              <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>Category *</label>
              <input type="text" value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })} className="cosmic-input w-full" required />
            </div>
            <div>
              <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>Content *</label>
              <textarea value={formData.content} onChange={(e) => setFormData({ ...formData, content: e.target.value })} className="cosmic-input w-full" rows="8" required />
            </div>
            <div>
              <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>Article Image</label>
              <div className="space-y-2">
                {formData.image_url && (
                  <div className="relative w-32 h-24 rounded-lg overflow-hidden" style={{ backgroundColor: `${currentColors.primary}10` }}>
                    <img 
                      src={formData.image_url.startsWith('/') ? `${API.replace('/api', '')}${formData.image_url}` : formData.image_url} 
                      alt="Article" 
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, image_url: '' })}
                      className="absolute top-1 right-1 p-1 rounded-full bg-red-500 text-white hover:bg-red-600"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                )}
                <div className="flex gap-2">
                  <input
                    type="file"
                    ref={imageInputRef}
                    onChange={handleImageUpload}
                    accept="image/*"
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => imageInputRef.current?.click()}
                    disabled={uploadingImage}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all"
                    style={{ backgroundColor: `${currentColors.primary}20`, color: currentColors.primary }}
                  >
                    {uploadingImage ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    {uploadingImage ? 'Uploading...' : 'Upload'}
                  </button>
                  <input 
                    type="url" 
                    value={formData.image_url} 
                    onChange={(e) => setFormData({ ...formData, image_url: e.target.value })} 
                    className="cosmic-input flex-1" 
                    placeholder="Or paste image URL..."
                  />
                </div>
              </div>
            </div>
            
            {/* Premium/Free Toggle */}
            <div 
              className="p-4 rounded-lg flex items-center justify-between"
              style={{ 
                backgroundColor: formData.is_premium ? `${currentColors.accent}10` : 'rgba(34, 197, 94, 0.1)',
                border: `1px solid ${formData.is_premium ? `${currentColors.accent}30` : 'rgba(34, 197, 94, 0.3)'}`
              }}
            >
              <div className="flex items-center gap-3">
                {formData.is_premium ? (
                  <Lock className="w-5 h-5" style={{ color: currentColors.accent }} />
                ) : (
                  <Unlock className="w-5 h-5 text-green-500" />
                )}
                <div>
                  <p className="font-medium" style={{ color: currentColors.text }}>
                    {formData.is_premium ? 'Premium Content' : 'Free Content'}
                  </p>
                  <p className="text-xs" style={{ color: currentColors.textMuted }}>
                    {formData.is_premium 
                      ? 'Only users with premium access can view this article' 
                      : 'This article is available to all users'}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, is_premium: !formData.is_premium })}
                className="relative w-14 h-7 rounded-full transition-all"
                data-testid="article-premium-toggle"
                style={{ 
                  backgroundColor: formData.is_premium ? currentColors.accent : '#22c55e'
                }}
              >
                <div 
                  className={`absolute w-5 h-5 bg-white rounded-full top-1 transition-all ${
                    formData.is_premium ? 'right-1' : 'left-1'
                  }`}
                />
              </button>
            </div>

            <button type="submit" className="cosmic-btn w-full flex items-center justify-center gap-2">
              <Save className="w-5 h-5" />
              <span>{editingId ? 'Update Article' : 'Create Article'}</span>
            </button>
          </form>
        </div>
      )}

      <div className="glass-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr style={{ borderBottom: `1px solid ${currentColors.border}` }}>
                <th className="text-left py-3 px-4 text-sm font-cinzel" style={{ color: currentColors.primary }}>Title</th>
                <th className="text-left py-3 px-4 text-sm font-cinzel" style={{ color: currentColors.primary }}>Author</th>
                <th className="text-left py-3 px-4 text-sm font-cinzel" style={{ color: currentColors.primary }}>Category</th>
                <th className="text-left py-3 px-4 text-sm font-cinzel" style={{ color: currentColors.primary }}>Access</th>
                <th className="text-left py-3 px-4 text-sm font-cinzel" style={{ color: currentColors.primary }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {articles.map((article) => (
                <tr key={article.id} style={{ borderBottom: `1px solid ${currentColors.border}` }} className="hover:opacity-80">
                  <td className="py-3 px-4" style={{ color: currentColors.text }}>{article.title}</td>
                  <td className="py-3 px-4" style={{ color: currentColors.textMuted }}>{article.author}</td>
                  <td className="py-3 px-4">
                    <span className="px-3 py-1 text-xs rounded-full" style={{ backgroundColor: `${currentColors.secondary}20`, color: currentColors.secondary }}>
                      {article.category}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    {article.is_premium ? (
                      <span className="flex items-center gap-1 text-xs px-2 py-1 rounded-full" style={{ backgroundColor: `${currentColors.accent}20`, color: currentColors.accent }}>
                        <Lock className="w-3 h-3" /> Premium
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-xs px-2 py-1 rounded-full" style={{ backgroundColor: 'rgba(34, 197, 94, 0.2)', color: '#22c55e' }}>
                        <Unlock className="w-3 h-3" /> Free
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex gap-2">
                      <button onClick={() => handleEdit(article)} className="p-2 rounded-lg" style={{ color: currentColors.accent }}><Edit className="w-4 h-4" /></button>
                      <button onClick={() => handleDelete(article.id)} className="p-2 rounded-lg text-red-400 hover:bg-red-500/10"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {articles.length === 0 && (
                <tr><td colSpan="5" className="py-8 text-center" style={{ color: currentColors.textMuted }}>No articles found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
